<template>
  <div class="flex flex-col gap-1.5">
    <label :for="resolvedId" :class="labelClasses">
      <span>
        {{ label }}
        <span v-if="required" aria-hidden="true" class="ml-0.5 text-stroke-danger">*</span>
      </span>
      <span v-if="optional && !required" class="text-xs font-normal text-ink-muted">
        {{ t('form.optional') }}
      </span>
    </label>
    <div>
      <slot :id="resolvedId" :described-by="describedBy" :invalid="invalid" :required="required" />
    </div>
    <p v-if="hint && !error" :id="hintId" class="text-xs text-ink-muted">{{ hint }}</p>
    <p v-if="error" :id="errorId" role="alert" class="text-xs font-medium text-danger-700">
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import { cva } from 'class-variance-authority';
import { useId } from '@/composables/useId';
import { FORM_FIELD_KEY } from '@/components/form/form-field-context';

const labelVariants = cva(
  'flex items-center justify-between text-sm font-semibold text-ink-strong',
  {
    variants: {
      hideLabel: {
        true: 'sr-only',
        false: '',
      },
    },
    defaultVariants: { hideLabel: false },
  },
);

interface Props {
  label: string;
  id?: string | null;
  name?: string | null;
  hint?: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  hideLabel?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  name: null,
  hint: '',
  error: '',
  required: false,
  optional: false,
  hideLabel: false,
});

const { t } = useI18n();
const fallbackId = useId('field');
const resolvedId = computed(() => props.id ?? fallbackId);
const hintId = computed(() => `${resolvedId.value}-hint`);
const errorId = computed(() => `${resolvedId.value}-error`);

const invalid = computed(() => Boolean(props.error));
const labelClasses = computed(() => labelVariants({ hideLabel: props.hideLabel }));

const describedBy = computed<string | undefined>(() => {
  const parts: string[] = [];
  if (props.hint) parts.push(hintId.value);
  if (props.error) parts.push(errorId.value);
  return parts.length ? parts.join(' ') : undefined;
});

provide(FORM_FIELD_KEY, {
  id: resolvedId,
  describedBy,
  invalid,
  required: computed(() => props.required),
});
</script>
